import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { briefStart, briefGetQuota, type BriefGenre, type BriefQuota } from '@ghostwright/api-client';

const GENRES: { value: BriefGenre; label: string }[] = [
  { value: 'fantasy', label: 'Fantasy' },
  { value: 'historical', label: 'Historical' },
  { value: 'horror', label: 'Horror' },
  { value: 'literary-speculative', label: 'Literary Speculative' },
  { value: 'literary-thriller', label: 'Literary Thriller' },
  { value: 'mystery', label: 'Mystery' },
  { value: 'romance', label: 'Romance' },
  { value: 'romantasy', label: 'Romantasy' },
  { value: 'sci-fi', label: 'Science Fiction' },
  { value: 'ya', label: 'Young Adult' },
];

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [genre, setGenre] = useState<BriefGenre | ''>('');
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [quota, setQuota] = useState<BriefQuota | null>(null);

  useEffect(() => {
    briefGetQuota().then(setQuota).catch(() => {});
  }, []);

  const handleStartBook = async () => {
    if (!genre || starting) return;
    setStarting(true);
    setError(null);
    try {
      const response = await briefStart(genre);
      navigate(`/intake/${response.sessionId}`);
    } catch (err: unknown) {
      const e = err as { code?: string; sessionId?: string; message?: string };
      if (e.code === 'ACTIVE_SESSION_EXISTS' && e.sessionId) {
        navigate(`/intake/${e.sessionId}`);
      } else {
        setError(e.message ?? 'Failed to start session');
      }
    } finally {
      setStarting(false);
    }
  };

  const quotaExhausted = quota && !quota.canStart;

  return (
    <div className="min-h-screen flex flex-col">
      <header className="border-b border-border px-5 sm:px-8 py-4">
        <div className="max-w-container mx-auto flex items-center justify-between">
          <a href="/" className="flex items-center gap-2 text-heading">
            <span className="font-display text-lg">Ghostwright</span>
            <span className="text-primary leading-none" aria-hidden="true">●</span>
          </a>
          <span className="text-sm text-muted-foreground">{user?.email}</span>
        </div>
      </header>

      <main className="flex-1 px-5 sm:px-8 py-12 sm:py-20">
        <div className="max-w-[720px] mx-auto">
          <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-3">
            <span className="text-primary mr-1.5" aria-hidden="true">§</span>
            Dashboard
          </p>
          <h1 className="font-display text-[clamp(28px,4vw,36px)] font-normal leading-[1.15] tracking-[-0.018em] text-heading mb-2 [font-variation-settings:'opsz'_96,'SOFT'_60]">
            Welcome back.
          </h1>
          <p className="text-base text-muted-foreground leading-relaxed mb-12">
            Your press is ready when you are.
          </p>

          <section>
            <h2 className="text-sm font-medium text-foreground mb-4">Your Books</h2>

            <div className="bg-card border border-border rounded-card p-8 text-center">
              {quotaExhausted ? (
                <>
                  <p className="text-muted-foreground text-[15px] leading-relaxed mb-2">
                    You've used your {quota.limit} free briefs.
                  </p>
                  <p className="text-muted-foreground text-[15px] leading-relaxed mb-6">
                    Buy a credit to start another book.
                  </p>
                  <a
                    href="/api/checkout/create"
                    className="inline-flex items-center gap-2 bg-primary text-primary-foreground font-medium text-sm py-3 px-6 rounded-button transition-opacity duration-80 hover:opacity-90"
                  >
                    Buy a Credit
                    <span aria-hidden="true">&rarr;</span>
                  </a>
                </>
              ) : (
                <>
                  <p className="text-muted-foreground text-[15px] leading-relaxed mb-2">
                    {quota && quota.used > 0
                      ? `${quota.used} of ${quota.limit} free briefs used.`
                      : 'No books yet. Start your first book.'}
                  </p>
                  {quota && quota.used > 0 && (
                    <p className="text-muted-foreground text-[13px] leading-relaxed mb-6">
                      {quota.limit - quota.used} free brief{quota.limit - quota.used !== 1 ? 's' : ''} remaining.
                    </p>
                  )}
                  {quota && quota.used === 0 && (
                    <p className="text-muted-foreground text-[13px] leading-relaxed mb-6">
                      You get {quota.limit} free briefs to explore.
                    </p>
                  )}

                  <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                    <select
                      value={genre}
                      onChange={(e) => setGenre(e.target.value as BriefGenre)}
                      className="text-sm py-2.5 px-4 border border-border rounded-button bg-background text-foreground min-w-[180px]"
                    >
                      <option value="">Select a genre</option>
                      {GENRES.map((g) => (
                        <option key={g.value} value={g.value}>{g.label}</option>
                      ))}
                    </select>

                    <button
                      type="button"
                      disabled={!genre || starting}
                      onClick={handleStartBook}
                      className="inline-flex items-center gap-2 bg-primary text-primary-foreground font-medium text-sm py-2.5 px-6 rounded-button transition-opacity duration-80 hover:opacity-90 disabled:opacity-50"
                    >
                      {starting ? 'Starting...' : 'Start a Book'}
                      <span aria-hidden="true">&rarr;</span>
                    </button>
                  </div>
                </>
              )}

              {error && (
                <p className="text-destructive text-sm mt-4">{error}</p>
              )}
            </div>
          </section>

        </div>
      </main>

      <footer className="border-t border-border px-5 sm:px-8 py-6 text-xs text-muted-foreground">
        <div className="max-w-container mx-auto flex items-center justify-between flex-wrap gap-3">
          <span>
            Ghostwright<span className="text-primary mx-1.5" aria-hidden="true">●</span>2026
          </span>
          <span>Dashboard</span>
        </div>
      </footer>
    </div>
  );
}
